// ===========================================
// BUTTON COMPONENT - Premium UI
// ===========================================

import React from 'react';
import { Loader2 } from 'lucide-react';

interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'secondary' | 'outline' | 'ghost' | 'danger' | 'success';
  size?: 'sm' | 'md' | 'lg' | 'xl';
  loading?: boolean;
  icon?: React.ReactNode;
  iconPosition?: 'left' | 'right';
  fullWidth?: boolean;
  glow?: boolean;
  children: React.ReactNode;
}

const Button: React.FC<ButtonProps> = ({
  variant = 'primary',
  size = 'md',
  loading = false,
  icon,
  iconPosition = 'left',
  fullWidth = false,
  glow = false,
  className = '',
  disabled,
  children,
  ...props
}) => {
  const baseStyles = `
    relative inline-flex items-center justify-center font-semibold
    transition-all duration-300 ease-out rounded-xl
    focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-tripnovac-500/50
    disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none
    transform hover:-translate-y-0.5 active:translate-y-0
  `;

  const variants = {
    primary: `
      bg-gradient-to-r from-tripnovac-600 via-tripnovac-500 to-violet-500
      text-white shadow-lg shadow-tripnovac-500/30
      hover:from-tripnovac-700 hover:via-tripnovac-600 hover:to-violet-600
      hover:shadow-xl hover:shadow-tripnovac-500/40
      ${glow ? 'animate-pulse-glow' : ''}
    `,
    secondary: `
      bg-gradient-to-r from-lilac-100 to-violet-100
      text-tripnovac-700 shadow-md
      hover:from-lilac-200 hover:to-violet-200
      hover:shadow-lg
    `,
    outline: `
      border-2 border-tripnovac-500/50 bg-transparent
      text-tripnovac-600 hover:bg-tripnovac-50
      hover:border-tripnovac-500 hover:shadow-lg hover:shadow-tripnovac-500/20
    `,
    ghost: `
      bg-transparent text-tripnovac-600
      hover:bg-tripnovac-50 hover:text-tripnovac-700
    `,
    danger: `
      bg-gradient-to-r from-red-600 to-rose-500
      text-white shadow-lg shadow-red-500/30
      hover:from-red-700 hover:to-rose-600
      hover:shadow-xl hover:shadow-red-500/40
    `,
    success: `
      bg-gradient-to-r from-emerald-600 to-green-500
      text-white shadow-lg shadow-emerald-500/30
      hover:from-emerald-700 hover:to-green-600
      hover:shadow-xl hover:shadow-emerald-500/40
    `,
  };

  const sizes = {
    sm: 'text-xs px-3 py-1.5 gap-1.5',
    md: 'text-sm px-4 py-2.5 gap-2',
    lg: 'text-base px-6 py-3 gap-2.5',
    xl: 'text-lg px-8 py-4 gap-3',
  };

  return (
    <button
      className={`
        ${baseStyles}
        ${variants[variant]}
        ${sizes[size]}
        ${fullWidth ? 'w-full' : ''}
        ${className}
      `}
      disabled={disabled || loading}
      {...props}
    >
      {loading && (
        <Loader2 className="w-4 h-4 animate-spin" />
      )}
      {!loading && icon && iconPosition === 'left' && icon}
      <span>{children}</span>
      {!loading && icon && iconPosition === 'right' && icon}
    </button>
  );
};

export default Button;
